import {IWikiFormatterProps} from '@yandex-int/tools-components/WikiFormatter';

import {PageDetailsField, PageType, TranslateLang, UserPermissions} from '~shared/models';

import {UpdatePageArgs} from '~shared/schema/wiki/actions/update-page-details';

import {GrantAuthorRoleArgs} from '~shared/schema/wiki/actions/access-request';

import {i18n} from '~ui/components/PageBackgroundPopup/i18n';
import {i18n as pageI18n} from '~ui/components/WikiPage/i18n';
import {getPageDetails, updatePageDetails, getPageDetailsById} from '~ui/services/pages';
import {createThunk, makeThunkErrorHandler} from '~ui/store/utils';
import toaster from '~ui/utils/toaster';

import {getRevisions} from '../revisions';

import {actions} from './slice';

import {
    updateRevisionLocationParams,
    SetRevisionsData,
    SetPreviewYfmData,
    updatePreviewYfmLocationParams,
} from './utils';

export const getPageAcl = createThunk('pages/get-acl', ({slug}: {slug: string}) => {
    return getPageDetails({
        slug,
        fields: [PageDetailsField.Acl, PageDetailsField.Access],
    });
});

export const updatePageSettings = createThunk(
    'pages/update-settings',
    async (
        {
            pageId,
            slug,
            acl,
            authors,
            redirect,
            preventSelflock,
            forceUpdateDetails,
            isCollaborative,
            commentsEnabled,
        }: Pick<
            UpdatePageArgs,
            | 'pageId'
            | 'commentsEnabled'
            | PageDetailsField.Acl
            | 'preventSelflock'
            | 'isCollaborative'
            | PageDetailsField.Authors
            | PageDetailsField.Redirect
        > & {
            slug: string;
            forceUpdateDetails?: boolean;
            forceRedirect?: boolean;
        },
        {dispatch},
    ) => {
        const res = await updatePageDetails({
            preventSelflock,
            pageId,
            acl,
            authors,
            redirect,
            isCollaborative,
            commentsEnabled,
            fields: [
                PageDetailsField.Acl,
                PageDetailsField.Access,
                PageDetailsField.Permissions,
                PageDetailsField.Authors,
                PageDetailsField.Attributes,
                PageDetailsField.Redirect,
            ],
        });

        if (
            preventSelflock === 0 ||
            (forceUpdateDetails && res.user_permissions.includes(UserPermissions.CanView))
        ) {
            dispatch(actions.getDetails({slug}));
        }
        return res;
    },
);

export const updatePageBackground = createThunk(
    'pages/update-background',
    ({
        pageId,
        background,
    }: Pick<UpdatePageArgs, 'pageId' | PageDetailsField.Background> & {
        prevBackground: number | undefined;
    }) => {
        return updatePageDetails({pageId, background, fields: [PageDetailsField.Background]})
            .then(({background}) => background?.id)
            .catch(
                makeThunkErrorHandler({
                    name: 'update-background-error',
                    title: i18n('update-error'),
                }),
            );
    },
);

export const updatePageRedirect = createThunk(
    'pages/update-redirect',
    ({
        pageId,
        redirect,
    }: Pick<UpdatePageArgs, 'pageId' | PageDetailsField.Redirect> & {forceRedirect?: boolean}) => {
        return updatePageDetails({pageId, redirect, fields: [PageDetailsField.Redirect]})
            .then(() => {
                toaster.createToast({
                    name: 'update-redirect',
                    type: 'success',
                    title: pageI18n('success-update-redirect'),
                });
            })
            .catch(
                makeThunkErrorHandler({
                    name: 'update-redirect-error',
                    title: pageI18n('error-update-redirect'),
                }),
            );
    },
);

export const updatePageContent = createThunk(
    'pages/update-content',
    ({
        pageId,
        content,
        revision,
        allowMerge,
    }: Pick<UpdatePageArgs, 'pageId' | 'revision' | 'allowMerge' | PageDetailsField.Content> & {
        pageType: PageType;
    }) => {
        return updatePageDetails({
            pageId,
            content,
            revision,
            allowMerge,
            fields: [PageDetailsField.Content, PageDetailsField.LastRevisionId],
        });
    },
);

export const translatePage = createThunk(
    'pages/translate',
    (
        {title, html, lang}: {id: number; title: string; html: string; lang: TranslateLang},
        {extra: {sdk}},
    ) => {
        return Promise.all([
            sdk.translate.translateHTML({
                text: html,
                lang,
            }),
            sdk.translate.translateText({
                text: title,
                lang,
            }),
        ])
            .then(([htmlResponse, titleResponse]) => {
                return {
                    title: titleResponse?.text?.[0] || title,
                    html: htmlResponse?.text?.[0] || html,
                };
            })
            .catch(
                makeThunkErrorHandler({
                    name: 'translate',
                    title: pageI18n('error-translate'),
                }),
            );
    },
);

export const updatePageRevisions = createThunk(
    'pages/update-revisions',
    ({revision1, revision2, id}: SetRevisionsData, {dispatch}) => {
        updateRevisionLocationParams({revision1, revision2});
        dispatch(actions.setRevisionsData({revision1, revision2, id}));
    },
);

export const updatePagePreviewYfm = createThunk(
    'pages/update-preview-yfm',
    ({previewYfm, id}: SetPreviewYfmData, {dispatch}) => {
        updatePreviewYfmLocationParams({previewYfm});
        dispatch(actions.setPreviewYfm({previewYfm, id}));
    },
);

export const grantAuthorRole = createThunk(
    'pages/grant-author-role',
    ({pageId, user}: GrantAuthorRoleArgs, {dispatch, extra: {sdk}}) => {
        return sdk.wiki
            .grantAuthorRole({
                pageId,
                user,
            })
            .then(() => {
                dispatch(actions.updateAuthors({id: pageId}));
            });
    },
);

export const updatePageActuality = createThunk(
    'pages/update-actuality',
    ({pageId, actuality}: Pick<UpdatePageArgs, 'pageId' | PageDetailsField.Actuality>) => {
        return updatePageDetails({
            pageId,
            actuality,
            fields: [PageDetailsField.Actuality, PageDetailsField.Attributes],
        });
    },
);

export const updatePageType = createThunk(
    'pages/update-type',
    (
        {
            pageId,
            content,
            wfSettings,
        }: Pick<UpdatePageArgs, 'pageId' | PageDetailsField.Content> & {
            wfSettings: IWikiFormatterProps['settings'];
        },
        {dispatch, extra: {sdk}},
    ) => {
        return sdk.wfaas
            .formatWikiToYfm({
                raw: content || '',
                settings: wfSettings || {},
            })
            .then((res) => {
                return sdk.wiki.changeToYfm({
                    pageId,
                    content: res.result,
                });
            })
            .then(() => {
                return getPageDetailsById({
                    pageId,
                    fields: [PageDetailsField.LastRevisionId, PageDetailsField.Content],
                });
            })
            .then((res) => {
                dispatch(getRevisions({pageId}));

                return res;
            })
            .catch((error) => {
                console.error('[thunk error]:', error);

                throw error;
            });
    },
);
